import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { addDays, differenceInDays } from "date-fns";

interface Payment {
  paid_at: string;
  validity_days: number;
}

interface MemberData {
  id: string;
  first_name: string | null;
  last_name: string | null;
  dni: string;
  avatar_url?: string | null;
}

export interface CategoryStatus {
  name: string;
  daysRemaining: number;
  expiresAt: string;
  isExpired: boolean;
}

export interface MemberLookupResult {
  member: MemberData;
  payment: Payment | null;
  isPending: boolean;
  paymentStatus: PaymentStatus;
  isAmarelo?: boolean;
  categoryStatuses?: CategoryStatus[];
  hasInscripcion?: boolean;
}

export interface PaymentStatus {
  status: 'active' | 'expiring' | 'expired';
  label: string;
  icon: string;
  color: 'green' | 'yellow' | 'red';
  daysRemaining?: number;
}

const getPaymentStatus = (payment: Payment | null): PaymentStatus => {
  if (!payment) {
    return { 
      status: 'expired', 
      label: 'Sin pagos', 
      icon: '❌', 
      color: 'red' 
    };
  }

  const expirationDate = addDays(new Date(payment.paid_at), payment.validity_days);
  const now = new Date();
  const daysUntilExpiration = differenceInDays(expirationDate, now);

  if (daysUntilExpiration < 0) {
    return { 
      status: 'expired', 
      label: 'Vencida', 
      icon: '❌', 
      color: 'red',
      daysRemaining: daysUntilExpiration
    };
  }

  if (daysUntilExpiration <= 5) {
    return { 
      status: 'expiring', 
      label: 'Próxima a vencer', 
      icon: '⚠️', 
      color: 'yellow',
      daysRemaining: daysUntilExpiration
    };
  }

  return { 
    status: 'active', 
    label: 'Al día', 
    icon: '✅', 
    color: 'green',
    daysRemaining: daysUntilExpiration
  };
};

// For Amarelo: calculate status based on category statuses (ignoring Inscripción)
const getAmareloPaymentStatus = (categoryStatuses: CategoryStatus[]): PaymentStatus => {
  // Filter out Inscripción - it shouldn't affect membership status
  const membershipCategories = categoryStatuses.filter(c => c.name !== 'Inscripción');
  
  if (membershipCategories.length === 0) {
    return { 
      status: 'expired', 
      label: 'Sin membresía', 
      icon: '❌', 
      color: 'red' 
    };
  }
  
  // Check if any membership is active
  const activeCategories = membershipCategories.filter(c => !c.isExpired);
  
  if (activeCategories.length > 0) {
    // Find the one expiring soonest
    const soonest = activeCategories.reduce((min, c) => 
      c.daysRemaining < min.daysRemaining ? c : min
    );
    
    if (soonest.daysRemaining <= 5) {
      return { 
        status: 'expiring', 
        label: 'Próxima a vencer', 
        icon: '⚠️', 
        color: 'yellow',
        daysRemaining: soonest.daysRemaining
      };
    }
    
    return { 
      status: 'active', 
      label: 'Al día', 
      icon: '✅', 
      color: 'green',
      daysRemaining: soonest.daysRemaining
    };
  }
  
  // All memberships expired - find the most recently expired
  const mostRecent = membershipCategories.reduce((max, c) => 
    c.daysRemaining > max.daysRemaining ? c : max
  );
  
  return { 
    status: 'expired', 
    label: 'Vencida', 
    icon: '❌', 
    color: 'red',
    daysRemaining: mostRecent.daysRemaining
  };
};

export interface LookupResponse {
  result: MemberLookupResult | null;
  notFound: boolean;
}

export const useMemberLookup = (gymId: string | null) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const lookup = async (dni: string): Promise<LookupResponse> => {
    if (!gymId || !dni.trim()) {
      return { result: null, notFound: false };
    }

    setIsLoading(true);
    setError(null);

    try {
      // Get current session for authorization
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        setError("No hay sesión activa");
        return { result: null, notFound: false };
      }

      // Call the edge function for secure lookup
      const response = await fetch(
        `https://esvhlvsfyrovwvkskfua.supabase.co/functions/v1/monitor-lookup`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({ dni: dni.trim() }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 404 && data.notFound) {
          // Member not found - this is expected, not an error
          return { result: null, notFound: true };
        }
        
        console.error("Lookup error:", data.error);
        setError(data.error || "Error al buscar el socio");
        return { result: null, notFound: false };
      }

      // Calculate payment status
      let paymentStatus: PaymentStatus;
      
      if (data.isAmarelo && data.categoryStatuses && data.categoryStatuses.length > 0) {
        // For Amarelo: use category-based status calculation
        paymentStatus = getAmareloPaymentStatus(data.categoryStatuses);
      } else {
        // For other gyms or no categories: use traditional payment-based status
        paymentStatus = getPaymentStatus(data.payment);
      }

      return {
        result: {
          member: data.member,
          payment: data.payment,
          isPending: data.isPending,
          paymentStatus,
          isAmarelo: data.isAmarelo,
          categoryStatuses: data.categoryStatuses,
          hasInscripcion: data.hasInscripcion
        },
        notFound: false
      };
    } catch (err) {
      console.error("Error looking up member:", err);
      setError("Error al buscar el socio");
      return { result: null, notFound: false };
    } finally {
      setIsLoading(false);
    }
  };

  return { lookup, isLoading, error };
};
